import {
    CalendarNav,
    CalendarNext, CalendarPrev,
    Eventcalendar,
    getJson,
    MbscCalendarEvent, MbscDateType,
    MbscEventcalendarView,
    MbscEventClickEvent, MbscPageLoadedEvent, Segmented, SegmentedGroup,
    setOptions,
    Toast,
} from '@mobiscroll/react';
import {ChangeEvent, FC, useCallback, useEffect, useMemo, useState} from 'react';
import {scheduledJobsService} from "../services/scheduledJobsService";
import {useNavigate} from "react-router-dom";
import FormSelectCustom from "../shared/components/FormSelectCustom";
import {toast} from "react-toastify";
import update = toast.update;
import {getBusinessOrCustomerName} from "../shared/sharedFunctions";

setOptions({
    theme: 'ios',
    themeVariant: 'light'
});

const transformToEvents = (data): any[] => {
    const events: any[] = [];

    data.jobDays.forEach((day) => {
        day.jobs.forEach((job) => {
            const name = getBusinessOrCustomerName(job);
            const startTime = `${job.dateDone}T00:01`;
            const endTime = `${job.dateDone}T23:59`;
            events.push({
                id: job.jobId,
                name,
                title: name,
                color: '#4caf50',
                start: startTime,
                end: endTime,
                amount: job.jobEstimate,
                type: job.propertyTypeName,
                comments: job.truckComments,
                original: job
            });
        });
    });
    return events;
};
const formatDateOnly = (d: Date): string => {
    if (d)
        return d.toISOString().split('T')[0];
    return null
}
const ScheduledJobsCalendar: FC = () => {
    const navigate = useNavigate()
    const [myEvents, setEvents] = useState<MbscCalendarEvent[]>([]);
    const [mySelectedDate, setSelectedDate] = useState<Date>(new Date());
    const [calendarType, setCalendarType] = useState<'week' | 'month' | 'year' | undefined>('week');

    const handleDateChange = (val: MbscDateType) => {
        // convert from whatever tf this type is to a date
        // have to ts ignore because the date property DOES exist but it likes to claim it doesnt????
        // @ts-ignore
        if(val.date){
            // @ts-ignore
            setSelectedDate(val.date)
        }
    };

    function renderAgenda(args) {
        const events = args[0]?.events ?? [];
        let total = events.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.original.amount;
        }, 0);
        const bigArray = Array(30)
            .fill(events)
            .flat();
        return (
            <div className='mbsc-event-group mbsc-ios'>
                <div className="d-flex justify-content-between align-items-end w-100 my-1">
                    <div className="mt-1">
                        <strong className='fs-6'>{args[0]?.date}</strong>
                        <div className='mt-1'>
                            <span className="fs-5 fw-normal">{events.length} job{events.length !== 1 ? 's' : ''}</span>
                            <strong className='ms-2 fs-5 fw-normal'> Total amount: ${total}</strong>
                        </div>
                    </div>
                    <select className="form-select form-select-sm w-auto sort-select" aria-label="Sort options">
                        <option value="price-desc">Sort by price highest to lowest</option>
                        <option value="price-asc">Sort by price lowest to highest</option>
                    </select>
                </div>
            </div>
        );
    }

    const customEvent = useCallback(
        (data) => (
            <>
                <div className="mds-agenda-event-title d-flex justify-content-between">
                    <p className="mb-0">{data.original.name}</p>
                    <div className="d-flex">
                        <p className="mb-0 me-2 small">{data.original.type}</p>
                        <p className="mb-0 rounded-pill bg-light text-body px-2 small">${data.original.amount}</p>
                    </div>
                </div>
                <div>
                    <p className="small opacity-50 mb-0 col-sm-6 col-xl-12">{data.original.comments ?? 'No Truck Comments'}</p>
                </div>
            </>
        ),
        [],
    );
    const fetchData = async (startDate, endDate = null) => {
        try {

            scheduledJobsService.getScheduledJobs(formatDateOnly(startDate), formatDateOnly(endDate)).then(r => {
                if(!r) return;
                setEvents(sortByPrice(transformToEvents(r), 'price-desc'))
            })
        } catch (err) {
            console.error('Failed to load scheduled jobs:', err);
        }
    };
    
    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setCalendarType(event.target.value as 'week' | 'month' | 'year' | undefined);
    }, []);

    const calendarHeaderSwitch = useCallback(
        () => (
            <>
                <CalendarNav className="custom-view-nav" />
                <div className="mbsc-flex mbsc-flex-1-0 mbsc-justify-content-center">
                    <SegmentedGroup value={calendarType} onChange={handleChange}>
                        <Segmented value="week" className="md-view-change"><i className="fas fa-calendar-week me-1"/> Week</Segmented>
                        <Segmented value="month" className="md-view-change"><i className="fas fa-calendar-days me-1"/> Month</Segmented>
                    </SegmentedGroup>
                </div>
                <CalendarPrev />
                <CalendarNext />
            </>
        ),
        [calendarType, handleChange],
    );
    const myView = useMemo<MbscEventcalendarView>(
        () => ({
            calendar: { type: calendarType },
            agenda: { type: 'day', scrollable: true},
        }),
        [calendarType],
    );

    const handleEventClick = useCallback((args: MbscEventClickEvent) => {
        const originalData = args.event.original
        const jobId = originalData.jobId;
        const customerId = originalData.customerId;
        navigate(`/admin/job-details`, {
            state: {
                jobId: jobId,
                customerId: customerId,
                customerName: originalData.businessName === '' ? `${originalData.firstName ?? ''} ${originalData.lastName}` : originalData.businessName,
            }
        })
    }, []);

    function sortByPrice(events, order) {
        const sorted = [...events].sort((a, b) =>
            order === 'price-asc' ? a.amount - b.amount : b.amount - a.amount
        );
        const result = [];
        let currentRank = 1;
        sorted.forEach((job, idx) => {
            if (idx > 0 && job.amount !== sorted[idx - 1].amount) {
                currentRank += 1;
            }
            result.push({ ...job, order: currentRank });
        });
        return result;
    }
    
    useEffect(() => {
        const pad = (n: number) => String(n).padStart(2, '0');
        const y = mySelectedDate.getFullYear();
        const m = pad(mySelectedDate.getMonth() + 1);
        const d = pad(mySelectedDate.getDate());
        const selectedDateFormat = `${y}-${m}-${d}`;
        const dayOfEvents = myEvents.filter(e => e.original.dateDone === selectedDateFormat)
        if(dayOfEvents.length > 0){
            setTimeout(() => {
                const container = document.getElementById('appointment-agenda');
                const header = container.querySelector('.mbsc-list-header');
                let total = 0;

                if (header && dayOfEvents.length > 0) {
                    if (!header.hasAttribute('data-original')) {
                        header.setAttribute('data-original', header.textContent.trim());
                    }
                    const originalText = header.getAttribute('data-original');

                    header.innerHTML =
                        '<div class="d-flex justify-content-between align-items-end w-100 my-1">' +
                        '<div class="mt-1">' +
                        '<span class="fs-6">' + originalText + '</span>' +
                        '<div class="mt-1">' +
                        '<span class="fs-5 fw-normal">' + dayOfEvents.length + ' job' + (dayOfEvents.length !== 1 ? 's' : '') + '</span>' +
                        '<strong class="ms-2 fs-5 fw-normal"> Total amount: $' + total + '</strong>' +
                        '</div>' +
                        '</div>' +
                        '<select class="form-select form-select-sm w-auto sort-select" aria-label="Sort options">' +
                        '<option value="price-desc">Sort by price highest to lowest</option>' +
                        '<option value="price-asc">Sort by price lowest to highest</option>' +
                        '</select>' +
                        '</div>';
                    const select = header.querySelector('.sort-select') as HTMLSelectElement;
                    select.addEventListener('change', () => {
                        const sortValue = select.value;
                        setEvents(sortByPrice(myEvents, sortValue))
                    });
                }
            }, 50);
            
        }
    }, [mySelectedDate]);

    return (
        <div id="appointment-agenda" style={{height: "80vh"}}>
            <Eventcalendar selectedDate={mySelectedDate} onSelectedDateChange={handleDateChange} renderEventContent={customEvent} renderHeader={calendarHeaderSwitch} onPageLoading={(args) => fetchData(args.firstDay, args.lastDay)} data={myEvents} view={myView} onEventClick={handleEventClick}/>
        </div>
    );
};
export default ScheduledJobsCalendar;